import React, { useEffect, useState } from 'react';
import { revenueAPI } from '../../lib/api';
import { formatCurrency, formatCurrencyFull, formatNumber, formatMonth } from '../../lib/utils';
import { compactInr } from '../../lib/chartFormat';
import { useTheme } from '../../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { 
    IndianRupee,
    DollarSign, 
    TrendingUp,
    Globe,
    Music2,
    Loader2
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#7C3AED', '#00FF9D', '#CCFF00', '#00F2EA', '#FFB700', '#FF4444'];

const LabelRevenuePage = () => {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState('all');
    const { theme } = useTheme();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await revenueAPI.getSummary();
                setSummary(response.data);
            } catch (error) {
                console.error('Failed to fetch revenue:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-[50vh]">
                <Loader2 className="w-8 h-8 animate-spin text-secondary" />
            </div>
        );
    }

    const monthlyData = summary?.by_month?.slice(0, 6).reverse() || [];
    const platformData = summary?.by_platform?.slice(0, 6) || [];
    const countryData = summary?.by_country?.slice(0, 5) || [];
    const availableMonths = summary?.by_month?.map(m => m.month) || [];

    // Theme-aware chart palette (fixes light-mode invisible axis/tooltip)
    const isDark = theme === 'dark';
    const chartColors = {
        grid:       isDark ? 'rgba(255,255,255,0.10)' : 'rgba(15,23,42,0.10)',
        axis:       isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.70)',
        tipBg:      isDark ? '#0f172a' : '#ffffff',
        tipBorder:  isDark ? 'rgba(255,255,255,0.10)' : 'rgba(15,23,42,0.15)',
        tipText:    isDark ? '#e5e7eb' : '#0f172a',
        cursor:     isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)',
    };

    return (
        <div className="space-y-6" data-testid="label-revenue-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-heading font-bold tracking-tight">Revenue Reports</h1>
                    <p className="text-muted-foreground">Detailed breakdown of your music revenue</p>
                </div>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                    <SelectTrigger className="w-48 bg-card border-border/50" data-testid="month-filter">
                        <SelectValue placeholder="Select month" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Time</SelectItem>
                        {availableMonths.map((month) => (
                            <SelectItem key={month} value={month}>
                                {formatMonth(month)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card className="bg-card/50 border-border/40">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center">
                                <IndianRupee className="w-6 h-6 text-secondary" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Net Revenue (INR)</p>
                                <p className="text-2xl font-heading font-bold text-secondary">
                                    {formatCurrency(summary?.totals?.net_revenue_inr, 'INR')}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    Gross: {formatCurrency(summary?.totals?.gross_revenue_inr, 'INR')}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-card/50 border-border/40">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                                <DollarSign className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Net Revenue (USD)</p>
                                <p className="text-2xl font-heading font-bold">
                                    {formatCurrency(summary?.totals?.net_revenue_usd)}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    Gross: {formatCurrency(summary?.totals?.gross_revenue_usd)}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-card/50 border-border/40">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center">
                                <TrendingUp className="w-6 h-6 text-accent" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Total Streams</p>
                                <p className="text-2xl font-heading font-bold">
                                    {formatNumber(summary?.totals?.total_units)}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-card/50 border-border/40">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                                <Globe className="w-6 h-6 text-muted-foreground" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Revenue Share</p>
                                <p className="text-2xl font-heading font-bold">
                                    {summary?.totals?.label_share_percentage || 70}%
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Monthly Revenue */}
                <Card className="bg-card/50 border-border/40">
                    <CardHeader>
                        <CardTitle className="font-heading">Monthly Net Revenue</CardTitle>
                        <CardDescription>Last 6 months performance (your share)</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[280px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={monthlyData} margin={{ top: 10, right: 12, left: 12, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                                    <XAxis
                                        dataKey="month"
                                        tickFormatter={(val) => val ? formatMonth(val) : ''}
                                        stroke={chartColors.axis}
                                        tick={{ fill: chartColors.axis, fontSize: 12 }}
                                    />
                                    <YAxis
                                        stroke={chartColors.axis}
                                        tick={{ fill: chartColors.axis, fontSize: 12 }}
                                        tickFormatter={compactInr}
                                        width={70}
                                    />
                                    <Tooltip
                                        cursor={{ fill: chartColors.cursor }}
                                        contentStyle={{
                                            backgroundColor: chartColors.tipBg,
                                            border: `1px solid ${chartColors.tipBorder}`,
                                            borderRadius: '8px',
                                            color: chartColors.tipText,
                                        }}
                                        labelStyle={{ color: chartColors.tipText }}
                                        itemStyle={{ color: chartColors.tipText }}
                                        formatter={(value, name, item) => {
                                            const key = item?.dataKey || name;
                                            return [formatCurrencyFull(value, 'INR'), key === 'net_inr' ? 'Net Revenue' : 'Gross Revenue'];
                                        }}
                                        labelFormatter={(label) => formatMonth(label)}
                                    />
                                    <Bar dataKey="net_inr" name="Net Revenue" fill="#00FF9D" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Platform Distribution */}
                <Card className="bg-card/50 border-border/40">
                    <CardHeader>
                        <CardTitle className="font-heading">Platform Distribution</CardTitle>
                        <CardDescription>Revenue by streaming platform</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[280px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={platformData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={50}
                                        outerRadius={90}
                                        paddingAngle={5}
                                        dataKey="gross_inr"
                                        nameKey="platform"
                                    >
                                        {platformData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: chartColors.tipBg, 
                                            border: `1px solid ${chartColors.tipBorder}`,
                                            borderRadius: '8px',
                                            color: chartColors.tipText,
                                        }}
                                        labelStyle={{ color: chartColors.tipText }}
                                        formatter={(value) => formatCurrencyFull(value, 'INR')}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="flex flex-wrap gap-3 mt-2 justify-center">
                            {platformData.map((platform, index) => (
                                <div key={platform.platform} className="flex items-center gap-2 text-xs">
                                    <div 
                                        className="w-2 h-2 rounded-full" 
                                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                    />
                                    <span className="text-muted-foreground">{platform.platform}</span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Tables Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Tracks */}
                <Card className="bg-card/50 border-border/40">
                    <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <Music2 className="w-5 h-5 text-secondary" />
                            Top Tracks
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="rounded-lg border border-border/40 overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/20 hover:bg-muted/20">
                                        <TableHead className="font-mono text-xs">#</TableHead>
                                        <TableHead className="font-mono text-xs">Track</TableHead>
                                        <TableHead className="font-mono text-xs text-right">Revenue</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {summary?.top_tracks?.slice(0, 5).map((track, index) => (
                                        <TableRow key={index} className="hover:bg-muted/10 border-border/40">
                                            <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                                            <TableCell>
                                                <div>
                                                    <p className="font-medium truncate max-w-[200px]">{track.title}</p>
                                                    <p className="text-xs text-muted-foreground truncate">{track.artist}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right font-mono text-secondary">
                                                {formatCurrency(track.net_inr ?? 0, 'INR')}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>

                {/* Top Countries */}
                <Card className="bg-card/50 border-border/40">
                    <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <Globe className="w-5 h-5 text-primary" />
                            Top Countries
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="rounded-lg border border-border/40 overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/20 hover:bg-muted/20">
                                        <TableHead className="font-mono text-xs">#</TableHead>
                                        <TableHead className="font-mono text-xs">Country</TableHead>
                                        <TableHead className="font-mono text-xs text-right">Revenue</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {countryData.map((country, index) => (
                                        <TableRow key={index} className="hover:bg-muted/10 border-border/40">
                                            <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                                            <TableCell className="font-medium">{country.country}</TableCell>
                                            <TableCell className="text-right font-mono text-primary">
                                                {formatCurrency(country.net_inr ?? 0, 'INR')}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default LabelRevenuePage;
